import "../../Components/list-items/BookingItem.css";
import "./UsersList.css";
import {
  faCalendar,
  faEnvelope,
  faExclamationTriangle,
  faGift,
  faInfoCircle,
  faPaperPlane,
  faPhone,
  faPlus,
  faRankingStar,
  faSave,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Alert,
  Card,
  Col,
  Container,
  Modal,
  Row,
} from "react-bootstrap";
import { useToasts } from "../../contexts/toasts";
import {
  Button as AntButton,
  Form,
  Input,
  InputNumber,
  Pagination,
  Segmented,
  Select,
} from "antd";
import { Structures } from "../../api/Structure";
import { UserFavorites } from "../../types/Structure";
import { TailSpin } from "react-loader-spinner";
import { User } from "../../api/User";
import { Option } from "../../models/generics";
import { Bookings } from "../../api/Booking";
import { Notifications } from "../../api/Notifications";
import { useAuth } from "../../contexts/auth";
import { Roles } from "../../enums/Roles";
import { getSportName } from "../../utils/stringUtil";
import { FullUserDTO } from "../../types/User";
import itIT from "antd/lib/locale/it_IT";
import { ConfigProvider } from "antd";
import { debounce } from "lodash";
import AppSearch from "../../Components/common/AppSearch";

const formLayout = {
  labelCol: { xs: 24, md: 5 },
  wrapperCol: { xs: 24, md: 12 },
};

function UserAvatar({ name, familyName }: { name?: string; familyName?: string }) {
  const initials = `${name?.[0] ?? ""}${familyName?.[0] ?? ""}`.toUpperCase();
  return <div className="user-avatar">{initials || "?"}</div>;
}

export default function UsersList() {
  const { showToast } = useToasts();
  const { loggedUser } = useAuth();
  const isAdmin = useMemo(
    () => loggedUser?.role === Roles.Superadmin,
    [loggedUser]
  );
  const [users, setUsers] = useState<UserFavorites[]>();
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [reIssueLoading, setReIssueLoading] = useState(false);
  const [addUserModal, setAddUserModal] = useState(false);
  const [userForm] = Form.useForm();
  const [notifyForm] = Form.useForm();
  const [notifyUser, setNotifyUser] = useState<UserFavorites | null>(null);
  const [notifySending, setNotifySending] = useState(false);
  const [pointsForm] = Form.useForm();
  const [pointsUser, setPointsUser] = useState<UserFavorites | null>(null);
  const [pointsSending, setPointsSending] = useState(false);
  const [sports, setSports] = useState<Option[]>();
  const [totalUsers, setTotalUsers] = useState<number>();
  const [filters, setFilters] = useState<{
    pageSize: number;
    currentPage: number;
    searchText: string;
    scope: "circle" | "all";
  }>({
    pageSize: 20,
    currentPage: 1,
    searchText: "",
    scope: "circle",
  });
  const [selectedSport, setSelectedSport] = useState<number>();

  const initSports = useCallback(() => {
    Bookings.getUserSports().then(
      ({ data }) => {
        setSports(
          data.list.map((sport) => ({
            label: getSportName(sport.name),
            value: sport.id,
          }))
        );
        if (data.list.length === 1) {
          setSelectedSport(data.list[0].id);
        }
      },
      () => {
        showToast("sports-error", "Impossibile ottenere gli sport", "", "danger");
        setSports([]);
        setError(true);
      }
    );
  }, [showToast]);

  const initUsers = useCallback(() => {
    const urlParams = new URLSearchParams();
    urlParams.append("page", `${filters.currentPage}`);
    urlParams.append("limit", `${filters.pageSize}`);
    if (filters.searchText !== "") {
      urlParams.append("freeText", filters.searchText);
    }
    const request =
      isAdmin && filters.scope === "all"
        ? User.getAllPaginated(urlParams)
        : Structures.getHostFavoritesPaginated(urlParams);
    request.then(
      ({ data }) => {
        setUsers(data.items);
        setTotalUsers(data.totalItems);
      },
      () => {
        showToast("users-error", "Impossibile ottenere la lista degli utenti", "", "danger");
        setUsers([]);
        setError(true);
      }
    );
  }, [filters, isAdmin, showToast]);

  const reset = useCallback(() => {
    setError(false);
    setUsers(undefined);
    initUsers();
    initSports();
  }, [initUsers, initSports]);

  useEffect(() => {
    reset();
  }, [filters, reset]);

  const handlePageChange = useCallback((page: number) => {
    setFilters((prev) => ({ ...prev, currentPage: page }));
  }, []);

  const handlePageSizeChange = useCallback((_current: number, size: number) => {
    setFilters((prev) => ({ ...prev, pageSize: size, currentPage: 1 }));
  }, []);

  const handleResendAccessToken = useCallback(
    (user: FullUserDTO, email: string) =>
      (e: React.MouseEvent<HTMLButtonElement>) => {
        setReIssueLoading(true);
        User.reIssueAccessToken({ name: user.name, familyName: user.familyName, email }).then(
          ({ data }) => {
            setReIssueLoading(false);
            showToast("create-user-success", data.message, "", "success");
          },
          (err) => {
            setReIssueLoading(false);
            const message =
              err.response.data.message === "auth/email-already-exists"
                ? "Indirizzo e-mail già in uso"
                : "Errore nella creazione dell'utente, per favore riprova";
            showToast("create-user-error", message, "", "danger");
          }
        );
      },
    [showToast]
  );

  const handleNotifyModalClose = useCallback(() => {
    setNotifyUser(null);
    notifyForm.resetFields();
  }, [notifyForm]);

  const handleNotifySubmit = useCallback(
    (values: { title: string; message: string }) => {
      if (!notifyUser) return;
      setNotifySending(true);
      Notifications.sendToUser({
        userId: notifyUser.userId,
        title: values.title.trim(),
        message: values.message.trim(),
      }).then(
        () => {
          setNotifySending(false);
          handleNotifyModalClose();
          showToast(
            "notify-user-success",
            "Notifica inviata correttamente.",
            "",
            "success"
          );
        },
        () => {
          setNotifySending(false);
          showToast(
            "notify-user-error",
            "Errore nell'invio della notifica, per favore riprova.",
            "",
            "danger"
          );
        }
      );
    },
    [notifyUser, handleNotifyModalClose, showToast]
  );

  const handlePointsModalClose = useCallback(() => {
    setPointsUser(null);
    pointsForm.resetFields();
  }, [pointsForm]);

  const handlePointsSubmit = useCallback(
    (values: { points: number }) => {
      if (!pointsUser) return;
      setPointsSending(true);
      User.addPoints(pointsUser.userId, values.points).then(
        ({ data }) => {
          setPointsSending(false);
          // Aggiorna in locale i punti dell'utente senza ricaricare la lista.
          setUsers((prev) =>
            prev?.map((u) =>
              u.userId === pointsUser.userId
                ? { ...u, user: { ...u.user, giftPoints: data.giftPoints } }
                : u
            )
          );
          handlePointsModalClose();
          showToast(
            "add-points-success",
            "Get Move Points aggiunti correttamente.",
            "",
            "success"
          );
        },
        () => {
          setPointsSending(false);
          showToast(
            "add-points-error",
            "Errore nell'aggiunta dei Get Move Points, per favore riprova.",
            "",
            "danger"
          );
        }
      );
    },
    [pointsUser, handlePointsModalClose, showToast]
  );

  const renderUsers = () => {
    if (error)
      return (
        <Alert variant="danger" className="py-2">
          <FontAwesomeIcon icon={faExclamationTriangle} />
          <span className="ms-2">C'è stato un errore, per favore riprova più tardi</span>
        </Alert>
      );

    if (users!.length === 0)
      return (
        <Alert variant="info" className="py-2">
          <FontAwesomeIcon icon={faInfoCircle} />
          <span className="ms-2">Nessun Utente trovato</span>
        </Alert>
      );

    return (
      <Row className="g-3">
        {users!.map((user, index) => (
          <Col xs={12} sm={6} md={4} lg={3} key={index}>
            <Card className="user-card">
              <Card.Body className="p-3">

                {/* Avatar + nome */}
                <div className="user-card-header">
                  <UserAvatar name={user.user.name} familyName={user.user.familyName} />
                  <div className="user-card-name">
                    <span className="user-name">
                      {user.user.name} {user.user.familyName}
                    </span>
                    {!user.user.active && (
                      <span className="user-inactive-badge">Non attivo</span>
                    )}
                  </div>
                </div>

                {/* Info */}
                <div className="user-card-info">
                  <div className="user-info-row">
                    <FontAwesomeIcon icon={faEnvelope} className="user-info-icon" />
                    <span className="user-info-text">{user.email}</span>
                  </div>

                  <div className={`user-info-row ${!user.user.phoneNumber ? "user-info-missing" : ""}`}>
                    <FontAwesomeIcon icon={faPhone} className="user-info-icon" />
                    <span className="user-info-text">
                      {user.user.phoneNumber ? `Tel. ${user.user.phoneNumber}` : "Telefono mancante"}
                    </span>
                  </div>

                  <div className={`user-info-row ${!user.user.birthDate ? "user-info-missing" : ""}`}>
                    <FontAwesomeIcon icon={faCalendar} className="user-info-icon" />
                    <span className="user-info-text">
                      {user.user.birthDate
                        ? new Date(user.user.birthDate).toLocaleDateString("it-IT")
                        : "Data nascita mancante"}
                    </span>
                  </div>

                  <div className={`user-info-row ${!user.user.level ? "user-info-missing" : ""}`}>
                    <FontAwesomeIcon icon={faRankingStar} className="user-info-icon" />
                    <span className="user-info-text">
                      {user.user.level ? `Livello ${user.user.level}` : "Livello mancante"}
                    </span>
                  </div>

                  <div className="user-info-row">
                    <FontAwesomeIcon icon={faGift} className="user-info-icon" />
                    <span className="user-info-text">
                      {`${user.user.giftPoints ?? 0} Get Move Points`}
                    </span>
                  </div>
                </div>

                {/* Azioni */}
                <div className="user-card-footer">
                  <AntButton
                    size="small"
                    icon={<FontAwesomeIcon icon={faPaperPlane} />}
                    onClick={() => setNotifyUser(user)}
                    style={{ width: "100%", fontSize: "0.75rem" }}
                  >
                    Invia notifica
                  </AntButton>

                  {isAdmin && (
                    <AntButton
                      size="small"
                      icon={<FontAwesomeIcon icon={faGift} />}
                      onClick={() => setPointsUser(user)}
                      style={{ width: "100%", fontSize: "0.75rem" }}
                    >
                      Aggiungi punti
                    </AntButton>
                  )}

                  {/* Reinvio mail (solo se non attivo) */}
                  {!user.user.active && (
                    <AntButton
                      size="small"
                      disabled={reIssueLoading}
                      onClick={handleResendAccessToken(user.user, user.email || "")}
                      style={{ width: "100%", fontSize: "0.75rem" }}
                    >
                      {reIssueLoading
                        ? <TailSpin width={14} height={14} />
                        : "Reinvia mail di conferma"}
                    </AntButton>
                  )}
                </div>

              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>
    );
  };

  const renderAsyncContent = () => {
    return users ? (
      renderUsers()
    ) : (
      <div className="d-flex justify-content-center py-5 my-5">
        <TailSpin height={100} width={100} color="#f25d0b" radius="1" />
      </div>
    );
  };

  const handleAddUserModalClose = useCallback(() => {
    setAddUserModal(false);
    userForm.resetFields();
  }, [userForm]);

  const handleFilterChange = useCallback((text: string) => {
    setFilters((prev) => ({ ...prev, currentPage: 1, searchText: text }));
  }, []);

  const handleScopeChange = useCallback((scope: "circle" | "all") => {
    setFilters((prev) => ({ ...prev, currentPage: 1, scope }));
  }, []);

  const debounceSearchChange = useMemo(
    () => debounce(handleFilterChange, 500),
    [handleFilterChange]
  );

  useEffect(() => {
    return () => { debounceSearchChange.cancel(); };
  }, [debounceSearchChange]);

  const handleCreateUserSubmit = useCallback(
    (values: any) => {
      setLoading(true);
      User.createUser({ ...values, sportId: selectedSport! }).then(
        ({ data }) => {
          setLoading(false);
          handleAddUserModalClose();
          reset();
          showToast("create-user-success", data.message, "", "success");
        },
        (err) => {
          const message =
            err.response.data.message === "auth/email-already-exists"
              ? "Indirizzo e-mail già in uso"
              : "Errore nella creazione dell'utente, per favore riprova";
          showToast("create-user-error", message, "", "danger");
          setLoading(false);
        }
      );
    },
    [selectedSport, reset, handleAddUserModalClose, showToast]
  );

  return (
    <Container fluid="md" className="mt-5">

      {/* Toolbar */}
      <div className="users-toolbar">
        <div className="users-toolbar-top">
          <h4 className="users-title">
            I tuoi Utenti
            {totalUsers !== undefined && (
              <span className="users-count-badge">{totalUsers}</span>
            )}
          </h4>
          <button onClick={() => setAddUserModal(true)} className="button-primary">
            <FontAwesomeIcon icon={faPlus} />
            <span className="d-none d-sm-inline ms-2">Aggiungi Utente</span>
          </button>
        </div>

        {isAdmin && (
          <Segmented
            options={[
              { label: "Il mio circolo", value: "circle" },
              { label: "Tutti gli utenti", value: "all" },
            ]}
            value={filters.scope}
            onChange={(value) => handleScopeChange(value as "circle" | "all")}
          />
        )}

        <div className="users-toolbar-bottom">
          <AppSearch
            className="w-100"
            placeholder="Cerca per nome, cognome o email..."
            onChange={debounceSearchChange}
          />
          <ConfigProvider locale={itIT}>
            <Pagination
              disabled={!users}
              total={totalUsers || 0}
              showTotal={(total, range) => `${range[0]}-${range[1]} di ${total}`}
              pageSize={filters.pageSize}
              current={filters.currentPage}
              onChange={handlePageChange}
              onShowSizeChange={handlePageSizeChange}
              showSizeChanger
              align="center"
            />
          </ConfigProvider>
        </div>
      </div>

      {renderAsyncContent()}

      {/* Modal aggiungi utente */}
      <Modal show={addUserModal} onHide={handleAddUserModalClose}>
        <Modal.Header closeButton>
          <Modal.Title>Aggiungi Utente</Modal.Title>
        </Modal.Header>
        <Form
          {...formLayout}
          form={userForm}
          onFinish={handleCreateUserSubmit}
          labelAlign="left"
        >
          <Modal.Body>
            <Form.Item
              label="Nome"
              name="name"
              className="mt-4"
              rules={[{ required: true, message: "Il campo nome è obbligatorio" }]}
            >
              <Input type="text" placeholder="Nome..." />
            </Form.Item>
            <Form.Item
              label="Cognome"
              name="familyName"
              className="mt-4"
              rules={[{ required: true, message: "Il campo cognome è obbligatorio" }]}
            >
              <Input type="text" placeholder="Cognome..." />
            </Form.Item>
            <Form.Item
              label="E-mail"
              name="email"
              className="mt-4"
              rules={[
                { required: true, message: "Il campo e-mail è obbligatorio" },
                { type: "email", message: "Inserisci una e-mail valida" },
              ]}
            >
              <Input type="text" placeholder="E-mail..." />
            </Form.Item>
            <Form.Item
              label="Sport"
              name="sportId"
              className="mt-4"
              rules={[{ required: true, message: "Devi scegliere per quale Sport aggiungere l'utente" }]}
            >
              <Select
                className="w-100"
                placeholder="Scegli lo Sport..."
                value={selectedSport}
                onSelect={setSelectedSport}
                allowClear
                options={sports}
                onDeselect={setSelectedSport}
                getPopupContainer={(trigger) => trigger.parentNode}
              />
            </Form.Item>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="light"
              size="sm"
              className="border"
              onClick={handleAddUserModalClose}
            >
              Chiudi
            </Button>
            <Button type="submit" variant="success" size="sm">
              {loading ? (
                <TailSpin width={20} height={20} />
              ) : (
                <>
                  <FontAwesomeIcon icon={faSave} />
                  <span> Salva</span>
                </>
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Modal invia notifica al singolo utente */}
      <Modal show={!!notifyUser} onHide={handleNotifyModalClose}>
        <Modal.Header closeButton>
          <Modal.Title>
            Invia notifica
            {notifyUser && (
              <span className="d-block text-muted" style={{ fontSize: "0.9rem" }}>
                {notifyUser.user.name} {notifyUser.user.familyName}
              </span>
            )}
          </Modal.Title>
        </Modal.Header>
        <Form
          {...formLayout}
          form={notifyForm}
          onFinish={handleNotifySubmit}
          labelAlign="left"
        >
          <Modal.Body>
            <Form.Item
              label="Titolo"
              name="title"
              className="mt-4"
              rules={[
                { required: true, message: "Inserisci un titolo" },
                { whitespace: true, message: "Inserisci un titolo valido" },
              ]}
            >
              <Input placeholder="Titolo della notifica..." maxLength={100} />
            </Form.Item>
            <Form.Item
              label="Messaggio"
              name="message"
              className="mt-4"
              rules={[
                { required: true, message: "Inserisci un messaggio" },
                { whitespace: true, message: "Inserisci un messaggio valido" },
              ]}
            >
              <Input.TextArea
                rows={5}
                placeholder="Testo della notifica..."
                maxLength={500}
                showCount
              />
            </Form.Item>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="light"
              size="sm"
              className="border"
              onClick={handleNotifyModalClose}
            >
              Chiudi
            </Button>
            <Button type="submit" variant="success" size="sm" disabled={notifySending}>
              {notifySending ? (
                <TailSpin width={20} height={20} />
              ) : (
                <>
                  <FontAwesomeIcon icon={faPaperPlane} />
                  <span> Invia</span>
                </>
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Modal aggiungi Get Move Points */}
      <Modal show={!!pointsUser} onHide={handlePointsModalClose}>
        <Modal.Header closeButton>
          <Modal.Title>
            Aggiungi Get Move Points
            {pointsUser && (
              <span className="d-block text-muted" style={{ fontSize: "0.9rem" }}>
                {pointsUser.user.name} {pointsUser.user.familyName} · attuali:{" "}
                {pointsUser.user.giftPoints ?? 0}
              </span>
            )}
          </Modal.Title>
        </Modal.Header>
        <Form
          {...formLayout}
          form={pointsForm}
          onFinish={handlePointsSubmit}
          labelAlign="left"
          initialValues={{ points: 1 }}
        >
          <Modal.Body>
            <Form.Item
              label="Punti"
              name="points"
              className="mt-4"
              rules={[
                { required: true, message: "Inserisci il numero di punti" },
                {
                  type: "number",
                  min: 1,
                  message: "Inserisci un numero di punti valido",
                },
              ]}
            >
              <InputNumber
                className="w-100"
                min={1}
                step={1}
                precision={0}
                placeholder="Numero di punti da aggiungere..."
              />
            </Form.Item>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="light"
              size="sm"
              className="border"
              onClick={handlePointsModalClose}
            >
              Chiudi
            </Button>
            <Button type="submit" variant="success" size="sm" disabled={pointsSending}>
              {pointsSending ? (
                <TailSpin width={20} height={20} />
              ) : (
                <>
                  <FontAwesomeIcon icon={faGift} />
                  <span> Aggiungi</span>
                </>
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

    </Container>
  );
}
